import {
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useState } from "react";
import api from "../../../../../services/api";
import { sampleReportKeys } from "../../../queryKeys";
import { isSampleAnalyzingOnRlcs } from "../../../util";

interface AnalysisResponse {
  display_status: string;
  analysis_status: string;
}

export interface RLcsAnalysisResponseData extends AnalysisResponse {
  analysis: boolean;
  analyzing: boolean;
}

interface RlcsAnalysisStatusProps {
  hash: string;
  refetchMergedReportFn: () => void;
  pollDisabled: boolean;
  options?: UseQueryOptions;
}

export const getRLCSAnalysisStatusQueryKey = (hash: string) =>
  sampleReportKeys.analysisStatus(hash);

const DEFAULT_REFETCH_INTERVAL = 15000;

export const useRlcsAnalysisStatus = ({
  hash,
  refetchMergedReportFn,
  pollDisabled,
  options
}: RlcsAnalysisStatusProps) => {
  const [enabled, setEnabled] = useState(!pollDisabled);
  const queryClient = useQueryClient();

  const analysisStatusQueryKey = getRLCSAnalysisStatusQueryKey(hash);
  const { data, refetch: refetchAnalysisStatus } = useQuery({
    initialData: {
      analyzing: false,
      display_status: null,
      analysis_status: null
    },
    queryKey: sampleReportKeys.analysisStatus(hash),
    queryFn: () =>
      api
        .rlSandboxDisplayStatus([hash])
        .then((r: AxiosResponse<AnalysisResponse>) => {
          const { display_status: displayStatus } = r.data;
          const isAnalysing = isSampleAnalyzingOnRlcs(displayStatus);
          const { display_status: previousDisplayStatus } =
            queryClient.getQueryData(
              analysisStatusQueryKey
            ) as AnalysisResponse;
          const isPreviousAnalysing = isSampleAnalyzingOnRlcs(
            previousDisplayStatus
          );
          const shouldRefetchReport = !isAnalysing && isPreviousAnalysing;
          setEnabled(!pollDisabled ? isAnalysing : false);
          if (shouldRefetchReport) refetchMergedReportFn();
          return { ...r.data, analyzing: isAnalysing };
        }),
    enabled,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
    ...options
  });

  return {
    analysisStatusData: data as RLcsAnalysisResponseData,
    refetchAnalysisStatus,
    analysisStatusQueryKey
  };
};
